import { z } from 'zod';
import type { LocalizedText, PermissionDefinition } from '../../permissions.ts';

/** Módulo Clientes (catálogo beadev-ai/catalogo): permissões, validações e tipos. */

export const PERMISSION_MODULES = {
  customers: { 'pt-BR': 'Clientes', en: 'Customers' },
} satisfies Record<string, LocalizedText>;

export const PERMISSIONS = [
  {
    code: 'customers.customer.read',
    module: 'customers',
    label: { 'pt-BR': 'Ver clientes', en: 'View customers' },
  },
  {
    code: 'customers.customer.manage',
    module: 'customers',
    label: {
      'pt-BR': 'Cadastrar, alterar e desativar clientes',
      en: 'Create, edit and deactivate customers',
    },
  },
] as const satisfies readonly PermissionDefinition[];

export const CUSTOMER_KINDS = ['person', 'company'] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];

const onlyDigits = (s: string) => s.replace(/\D/g, '');

/** CPF com os dígitos verificadores certos (só números, 11 dígitos). */
export function isValidCpf(value: string): boolean {
  const d = onlyDigits(value);
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false;
  const check = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return check(9) === Number(d[9]) && check(10) === Number(d[10]);
}

/** CNPJ com os dígitos verificadores certos (só números, 14 dígitos). */
export function isValidCnpj(value: string): boolean {
  const d = onlyDigits(value);
  if (!/^\d{14}$/.test(d) || /^(\d)\1{13}$/.test(d)) return false;
  const check = (len: number) => {
    const weights =
      len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + Number(d[i]) * w, 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return check(12) === Number(d[12]) && check(13) === Number(d[13]);
}

/** 12345678909 → 123.456.789-09; 11222333000181 → 11.222.333/0001-81. */
export function formatDocument(value: string): string {
  const d = onlyDigits(value);
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return value;
}

/** 5531999998888 → +55 31 99999-8888 (outros países: só o +). */
export function formatPhone(value: string): string {
  const d = onlyDigits(value);
  const br = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(d);
  return br ? `+55 ${br[1]} ${br[2]}-${br[3]}` : `+${d}`;
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => s || null)
    .nullable()
    .optional()
    .transform((s) => s ?? null);

export const customerInput = z
  .object({
    kind: z.enum(CUSTOMER_KINDS),
    name: z.string().trim().min(1).max(200),
    /** CPF (pessoa) ou CNPJ (empresa), com ou sem pontuação; vazio = sem documento. */
    document: optionalText(30).transform((s) => (s ? onlyDigits(s) : null)),
    email: optionalText(254).pipe(z.email().nullable()),
    /** Telefone com DDI e DDD, só números (5531999998888). */
    phone: optionalText(30)
      .transform((s) => (s ? onlyDigits(s) : null))
      .pipe(
        z
          .string()
          .regex(/^\d{10,15}$/)
          .nullable(),
      ),
    city: optionalText(120),
    notes: optionalText(2000),
    isActive: z.boolean().default(true),
  })
  .superRefine((c, ctx) => {
    if (!c.document) return;
    const ok = c.kind === 'person' ? isValidCpf(c.document) : isValidCnpj(c.document);
    if (!ok)
      ctx.addIssue({
        code: 'custom',
        path: ['document'],
        message: c.kind === 'person' ? 'CPF inválido' : 'CNPJ inválido',
      });
  });
export type CustomerInput = z.input<typeof customerInput>;

/** O cliente como a API devolve. */
export interface Customer {
  id: string;
  kind: CustomerKind;
  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
